import React, { Component } from "react";
import { MqttContext } from "../../Contexts/MqttProvider";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Skeleton,
  Chip,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  AccountBalanceWallet,
  AccessTime,
  BarChart,
  CurrencyBitcoin,
  EuroSymbol,
  InfoOutlined,
  Layers,
  MonetizationOn,
  Paid,
  RadioButtonChecked,
  Search,
  Security,
  ShowChart,
  StackedBarChart,
  TrackChanges,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import {
  apiAccountMetricPairTypes,
  apiDashboardAccountLiveProfit,
  apiDashboardAccountMaxFloating,
} from "../../Service/api";

const TRADE_SYMBOL_OVERRIDES = {
  117117224: "EURUSD",
  270886869: "XAUUSDm",
  270638630: "XAUUSDm",
  206985092: "XAUUSDm",
};

const BASE_CONFIG = {
  ALL: {
    label: "All",
    Icon: ShowChart,
    color: "#285b89",
    bg: "#edf2f8",
    border: "#d7e0ec",
  },
  EUR: {
    label: "EUR",
    Icon: EuroSymbol,
    color: "#1769aa",
    bg: "#e3f2fd",
    border: "#bbdefb",
  },
  XAU: {
    label: "XAU",
    Icon: MonetizationOn,
    color: "#946200",
    bg: "#fff4d6",
    border: "#f8dda0",
  },
  BTC: {
    label: "BTC",
    Icon: CurrencyBitcoin,
    color: "#a14f00",
    bg: "#fff1df",
    border: "#ffd4a6",
  },
  GBPJPY: {
    label: "GBPJPY",
    Icon: ShowChart,
    color: "#05745e",
    bg: "#e1f7f0",
    border: "#afe4d5",
  },
  OTHER: {
    label: "Other",
    Icon: AccountBalanceWallet,
    color: "#475569",
    bg: "#f1f5f9",
    border: "#dbe4ef",
  },
};

const ICON_MAP = {
  AccountBalanceWallet,
  CurrencyBitcoin,
  EuroSymbol,
  MonetizationOn,
  ShowChart,
};

const TAB_FLOATING_FONT_VARS = {
  "--tf-base-tab-font": "0.7rem",
  "--tf-pair-tab-font": "0.68rem",
  "--tf-title-font": "0.84rem",
  "--tf-meta-font": "0.58rem",
  "--tf-label-font": "0.64rem",
  "--tf-value-font": "0.9rem",
  "--tf-detail-title-font": "0.82rem",
  "--tf-detail-value-font": "0.8rem",
  "--tf-chip-font": "0.66rem",
  "--tf-price-font": "0.76rem",
  "--tf-icon-size": "14px",
  "--tf-metric-title-font": "0.64rem",
  "--tf-metric-value-font": "1.22rem",
  "--tf-metric-detail-label-font": "0.58rem",
  "--tf-metric-detail-value-font": "0.74rem",
  "--tf-metric-date-font": "0.52rem",
  "--tf-metric-pill-font": "0.56rem",
  "--tf-metric-icon-size": "26px",
  "@media (min-width:600px)": {
    "--tf-base-tab-font": "0.76rem",
    "--tf-pair-tab-font": "0.74rem",
    "--tf-title-font": "0.92rem",
    "--tf-meta-font": "0.64rem",
    "--tf-label-font": "0.65rem",
    "--tf-value-font": "0.88rem",
    "--tf-detail-title-font": "0.8rem",
    "--tf-detail-value-font": "0.78rem",
    "--tf-chip-font": "0.66rem",
    "--tf-price-font": "0.76rem",
    "--tf-icon-size": "14px",
    "--tf-metric-title-font": "0.7rem",
    "--tf-metric-value-font": "1.34rem",
    "--tf-metric-detail-label-font": "0.62rem",
    "--tf-metric-detail-value-font": "0.8rem",
    "--tf-metric-date-font": "0.54rem",
    "--tf-metric-pill-font": "0.66rem",
    "--tf-metric-icon-size": "32px",
  },
  "@media (min-width:900px)": {
    "--tf-base-tab-font": "0.82rem",
    "--tf-pair-tab-font": "0.8rem",
    "--tf-title-font": "1rem",
    "--tf-meta-font": "0.68rem",
    "--tf-label-font": "0.7rem",
    "--tf-value-font": "0.96rem",
    "--tf-detail-title-font": "0.86rem",
    "--tf-detail-value-font": "0.84rem",
    "--tf-chip-font": "0.7rem",
    "--tf-price-font": "0.82rem",
    "--tf-icon-size": "15px",
    "--tf-metric-title-font": "0.76rem",
    "--tf-metric-value-font": "1.5rem",
    "--tf-metric-detail-label-font": "0.66rem",
    "--tf-metric-detail-value-font": "0.9rem",
    "--tf-metric-date-font": "0.56rem",
    "--tf-metric-pill-font": "0.68rem",
    "--tf-metric-icon-size": "34px",
  },
  "@media (min-width:1200px)": {
    "--tf-base-tab-font": "0.88rem",
    "--tf-pair-tab-font": "0.86rem",
    "--tf-title-font": "1.08rem",
    "--tf-meta-font": "0.72rem",
    "--tf-label-font": "0.74rem",
    "--tf-value-font": "1.04rem",
    "--tf-detail-title-font": "0.92rem",
    "--tf-detail-value-font": "0.9rem",
    "--tf-chip-font": "0.74rem",
    "--tf-price-font": "0.88rem",
    "--tf-icon-size": "16px",
    "--tf-metric-title-font": "0.82rem",
    "--tf-metric-value-font": "1.62rem",
    "--tf-metric-detail-label-font": "0.68rem",
    "--tf-metric-detail-value-font": "0.96rem",
    "--tf-metric-date-font": "0.58rem",
    "--tf-metric-pill-font": "0.7rem",
    "--tf-metric-icon-size": "38px",
  },
  "@media (min-width:1536px)": {
    "--tf-base-tab-font": "0.94rem",
    "--tf-pair-tab-font": "0.92rem",
    "--tf-title-font": "1.16rem",
    "--tf-meta-font": "0.76rem",
    "--tf-label-font": "0.78rem",
    "--tf-value-font": "1.12rem",
    "--tf-detail-title-font": "0.98rem",
    "--tf-detail-value-font": "0.96rem",
    "--tf-chip-font": "0.78rem",
    "--tf-price-font": "0.94rem",
    "--tf-icon-size": "17px",
    "--tf-metric-title-font": "0.88rem",
    "--tf-metric-value-font": "1.78rem",
    "--tf-metric-detail-label-font": "0.72rem",
    "--tf-metric-detail-value-font": "1rem",
    "--tf-metric-date-font": "0.62rem",
    "--tf-metric-pill-font": "0.74rem",
    "--tf-metric-icon-size": "42px",
  },
  "@media (min-width:2200px)": {
    "--tf-base-tab-font": "1.02rem",
    "--tf-pair-tab-font": "1rem",
    "--tf-title-font": "1.28rem",
    "--tf-meta-font": "0.84rem",
    "--tf-label-font": "0.86rem",
    "--tf-value-font": "1.24rem",
    "--tf-detail-title-font": "1.06rem",
    "--tf-detail-value-font": "1.04rem",
    "--tf-chip-font": "0.86rem",
    "--tf-price-font": "1.02rem",
    "--tf-icon-size": "19px",
    "--tf-metric-title-font": "0.96rem",
    "--tf-metric-value-font": "2rem",
    "--tf-metric-detail-label-font": "0.78rem",
    "--tf-metric-detail-value-font": "1.1rem",
    "--tf-metric-date-font": "0.68rem",
    "--tf-metric-pill-font": "0.82rem",
    "--tf-metric-icon-size": "48px",
  },
};

const RESPONSIVE_TEXT = {
  baseTab: "var(--tf-base-tab-font)",
  pairTab: "var(--tf-pair-tab-font)",
  title: "var(--tf-title-font)",
  meta: "var(--tf-meta-font)",
  label: "var(--tf-label-font)",
  value: "var(--tf-value-font)",
  detailTitle: "var(--tf-detail-title-font)",
  detailValue: "var(--tf-detail-value-font)",
  chip: "var(--tf-chip-font)",
  price: "var(--tf-price-font)",
  icon: "var(--tf-icon-size)",
  metricTitle: "var(--tf-metric-title-font)",
  metricValue: "var(--tf-metric-value-font)",
  metricDetailLabel: "var(--tf-metric-detail-label-font)",
  metricDetailValue: "var(--tf-metric-detail-value-font)",
  metricDate: "var(--tf-metric-date-font)",
  metricPill: "var(--tf-metric-pill-font)",
  metricIcon: "var(--tf-metric-icon-size)",
};

class AccountMetricTabs extends Component {
  static contextType = MqttContext;

  constructor(props) {
    super(props);
    this.state = {
      activeAccountNumber: "",
      activeBase: "",
      pairTypes: [],
      isLoadingPairTypes: true,
      pairTypesError: null,
      searchKeyword: "",
      profitOverviewByAccount: {},
      maxFloatingByAccount: {},
    };
    this.subscribedMetricAccounts = new Set();
    this.subscribedTradeTopics = new Set();
    this.metricSubscriptionSignature = "";
    this.tradeSubscriptionSignature = "";
    this.isMountedComponent = false;
  }

  componentDidMount() {
    this.isMountedComponent = true;
    this.fetchPairTypes();
    this.fetchDashboardSummaries();
    this.syncActiveMetricSubscription();
  }

  componentDidUpdate() {
    this.syncActiveMetricSubscription();
  }

  componentWillUnmount() {
    this.isMountedComponent = false;
    this.unsubscribeMetricAccounts([...this.subscribedMetricAccounts]);
    this.unsubscribeTradeTopics([...this.subscribedTradeTopics]);
  }

  fetchPairTypes = async () => {
    try {
      const response = await apiAccountMetricPairTypes();
      const records =
        response &&
        response.data &&
        response.data.status === "OK" &&
        Array.isArray(response.data.records)
          ? response.data.records
          : [];

      const pairTypes = records
        .map((item) => ({
          accountNumber: String(
            item.account_number || item.account_master || "",
          ),
          key: item.key || "",
          label: item.label || item.key || item.account_master || "",
          base: item.base || "",
          capital:
            item.capital !== null && item.capital !== undefined
              ? Number(item.capital)
              : null,
          currency: item.currency || "",
          isCent:
            item.is_cent === true ||
            String(item.currency || "").toLowerCase() === "cents",
          tradeSymbol: item.trade_symbol || "",
          iconName: item.icon_name || "",
          bgColor: item.bg_color || "",
          textColor: item.text_color || "",
          borderColor: item.border_color || "",
          iconColor: item.icon_color || "",
        }))
        .filter((item) => item.accountNumber);

      if (!this.isMountedComponent) {
        return;
      }

      this.setState((prevState) => {
        const activeStillExists = pairTypes.some(
          (item) => item.accountNumber === prevState.activeAccountNumber,
        );
        const nextActiveAccountNumber = activeStillExists
          ? prevState.activeAccountNumber
          : (pairTypes[0] && pairTypes[0].accountNumber) || "";
        const nextActivePairType = pairTypes.find(
          (item) => item.accountNumber === nextActiveAccountNumber,
        );
        const nextActiveBase =
          prevState.activeBase || this.getBaseName(nextActivePairType);

        return {
          pairTypes,
          activeAccountNumber: nextActiveAccountNumber,
          activeBase: nextActiveBase || "ALL",
          isLoadingPairTypes: false,
          pairTypesError:
            pairTypes.length > 0
              ? null
              : "No pair types available. Please check your account settings.",
        };
      });
    } catch (error) {
      if (!this.isMountedComponent) {
        return;
      }

      this.setState({
        pairTypes: [],
        activeAccountNumber: "",
        activeBase: "",
        isLoadingPairTypes: false,
        pairTypesError: "failed to fetch pair types. Please try again later.",
      });
    }
  };

  fetchDashboardSummaries = async () => {
    try {
      const [profitResponse, maxFloatingResponse] = await Promise.all([
        apiDashboardAccountLiveProfit(),
        apiDashboardAccountMaxFloating(),
      ]);

      const profitRecords =
        profitResponse &&
        profitResponse.data &&
        profitResponse.data.status === "OK" &&
        Array.isArray(profitResponse.data.records)
          ? profitResponse.data.records
          : [];
      const maxFloatingRecords =
        maxFloatingResponse &&
        maxFloatingResponse.data &&
        maxFloatingResponse.data.status === "OK" &&
        Array.isArray(maxFloatingResponse.data.records)
          ? maxFloatingResponse.data.records
          : [];

      const profitOverviewByAccount = profitRecords.reduce((acc, item) => {
        const accountNumber = String(item.account_number || "");
        if (accountNumber) {
          acc[accountNumber] = item;
        }
        return acc;
      }, {});
      const maxFloatingByAccount = maxFloatingRecords.reduce((acc, item) => {
        const accountNumber = String(item.account_number || "");
        if (accountNumber) {
          acc[accountNumber] = item.maxFloating || {};
        }
        return acc;
      }, {});

      if (!this.isMountedComponent) {
        return;
      }

      this.setState({
        profitOverviewByAccount,
        maxFloatingByAccount,
      });
    } catch (error) {
      if (!this.isMountedComponent) {
        return;
      }

      this.setState({
        profitOverviewByAccount: {},
        maxFloatingByAccount: {},
      });
    }
  };

  subscribeMetricAccount = (accountNumber) => {
    const { subscribeToTopic, client } = this.context;
    if (!accountNumber || !client || !subscribeToTopic) {
      return;
    }

    subscribeToTopic(`Account/${accountNumber}/metricOntick`);
    subscribeToTopic(`Account/${accountNumber}/profit/utcp0`);
    this.subscribedMetricAccounts.add(accountNumber);
  };

  unsubscribeMetricAccount = (accountNumber) => {
    const { unsubscribeFromTopic } = this.context;
    if (!accountNumber || !unsubscribeFromTopic) {
      return;
    }

    unsubscribeFromTopic(`Account/${accountNumber}/metricOntick`);
    unsubscribeFromTopic(`Account/${accountNumber}/profit/utcp0`);
    this.subscribedMetricAccounts.delete(accountNumber);
  };

  unsubscribeMetricAccounts = (accountNumbers) => {
    accountNumbers.forEach((accountNumber) => {
      this.unsubscribeMetricAccount(accountNumber);
    });
  };

  subscribeTradeTopic = (topic) => {
    const { subscribeToTopic, client } = this.context;
    if (!topic || !client || !subscribeToTopic) {
      return;
    }

    subscribeToTopic(topic);
    this.subscribedTradeTopics.add(topic);
  };

  unsubscribeTradeTopic = (topic) => {
    const { unsubscribeFromTopic } = this.context;
    if (!topic || !unsubscribeFromTopic) {
      return;
    }

    unsubscribeFromTopic(topic);
    this.subscribedTradeTopics.delete(topic);
  };

  unsubscribeTradeTopics = (topics) => {
    topics.forEach((topic) => {
      this.unsubscribeTradeTopic(topic);
    });
  };

  getDesiredMetricAccounts = () => {
    const { activeAccountNumber } = this.state;

    if (!activeAccountNumber) {
      return [];
    }

    return [activeAccountNumber];
  };

  getDesiredTradeTopics = () => {
    const { activeAccountNumber } = this.state;
    const pairType = this.getPairTypeByAccountNumber(activeAccountNumber);
    const tradeSymbol = this.getTradeSymbol(pairType);

    if (!activeAccountNumber || !tradeSymbol) {
      return [];
    }

    return [`TradedataV2/${activeAccountNumber}/${tradeSymbol}`];
  };

  syncActiveMetricSubscription = () => {
    const { client } = this.context;
    if (!client) {
      return;
    }

    const desiredAccounts = new Set(this.getDesiredMetricAccounts());
    const nextSignature = [...desiredAccounts].sort().join("|");
    if (nextSignature !== this.metricSubscriptionSignature) {
      const currentAccounts = new Set(this.subscribedMetricAccounts);

      currentAccounts.forEach((accountNumber) => {
        if (!desiredAccounts.has(accountNumber)) {
          this.unsubscribeMetricAccount(accountNumber);
        }
      });

      desiredAccounts.forEach((accountNumber) => {
        if (!this.subscribedMetricAccounts.has(accountNumber)) {
          this.subscribeMetricAccount(accountNumber);
        }
      });

      this.metricSubscriptionSignature = nextSignature;
    }

    const desiredTradeTopics = new Set(this.getDesiredTradeTopics());
    const nextTradeSignature = [...desiredTradeTopics].sort().join("|");
    if (nextTradeSignature !== this.tradeSubscriptionSignature) {
      const currentTradeTopics = new Set(this.subscribedTradeTopics);

      currentTradeTopics.forEach((topic) => {
        if (!desiredTradeTopics.has(topic)) {
          this.unsubscribeTradeTopic(topic);
        }
      });

      desiredTradeTopics.forEach((topic) => {
        if (!this.subscribedTradeTopics.has(topic)) {
          this.subscribeTradeTopic(topic);
        }
      });

      this.tradeSubscriptionSignature = nextTradeSignature;
    }
  };

  getPairTypeByAccountNumber = (accountNumber) => {
    return this.state.pairTypes.find(
      (item) => item.accountNumber === String(accountNumber),
    );
  };

  getMetricDataForAccount = (accountNumber) => {
    const { messagesAccountMetric } = this.context;
    return (
      (messagesAccountMetric && messagesAccountMetric[accountNumber]) || null
    );
  };

  getProfitToday = (accountNumber) => {
    const { messagesAccountProfitToday } = this.context;
    const pairType = this.getPairTypeByAccountNumber(accountNumber);

    if (
      messagesAccountProfitToday &&
      messagesAccountProfitToday[accountNumber] &&
      typeof messagesAccountProfitToday[accountNumber].profit !== "undefined"
    ) {
      const rawProfit = Number(
        messagesAccountProfitToday[accountNumber].profit,
      );
      const isCentAccount = pairType && pairType.isCent;
      return isCentAccount ? rawProfit / 100 : rawProfit;
    }

    return null;
  };

  getBaseName = (pairType) => {
    const rawBase = String((pairType && pairType.base) || "").toUpperCase();
    const rawKey = String((pairType && pairType.key) || "").toUpperCase();
    const source = rawBase || rawKey;

    if (source.indexOf("BTC") >= 0) {
      return "BTC";
    }
    if (source.indexOf("XAU") >= 0 || source.indexOf("GOLD") >= 0) {
      return "XAU";
    }
    if (source.indexOf("GBPJPY") >= 0 || source.indexOf("GBP") >= 0) {
      return "GBPJPY";
    }
    if (source.indexOf("EUR") >= 0) {
      return "EUR";
    }

    return source || "OTHER";
  };

  getBaseConfig = (base) => {
    return BASE_CONFIG[base] || BASE_CONFIG.OTHER;
  };

  getPairStyle = (pairType) => {
    const baseConfig = this.getBaseConfig(this.getBaseName(pairType));

    return {
      bgColor: (pairType && pairType.bgColor) || baseConfig.bg,
      textColor: (pairType && pairType.textColor) || baseConfig.color,
      borderColor: (pairType && pairType.borderColor) || baseConfig.border,
      iconColor:
        (pairType && pairType.iconColor) ||
        (pairType && pairType.textColor) ||
        baseConfig.color,
    };
  };

  getPairIcon = (pairType) => {
    const iconName = pairType && pairType.iconName;
    if (iconName && ICON_MAP[iconName]) {
      return ICON_MAP[iconName];
    }

    return this.getBaseConfig(this.getBaseName(pairType)).Icon;
  };

  getBaseOptions = () => {
    const counts = this.state.pairTypes.reduce((acc, pairType) => {
      const base = this.getBaseName(pairType);
      acc[base] = (acc[base] || 0) + 1;
      return acc;
    }, {});

    const order = ["EUR", "XAU", "BTC", "GBPJPY"];
    const orderedBases = order
      .filter((base) => counts[base])
      .concat(
        Object.keys(counts)
          .filter((base) => order.indexOf(base) < 0)
          .sort(),
      );

    return [
      { value: "ALL", label: "All", count: this.state.pairTypes.length },
      ...orderedBases.map((base) => ({
        value: base,
        label: this.getBaseConfig(base).label,
        count: counts[base],
      })),
    ];
  };

  getFilteredPairTypesFor = (activeBase, searchKeyword) => {
    const keyword = String(searchKeyword || "")
      .trim()
      .toLowerCase();

    return this.state.pairTypes.filter((pairType) => {
      const baseMatched =
        activeBase === "ALL" || this.getBaseName(pairType) === activeBase;

      if (!baseMatched) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      return (
        [pairType.label, pairType.key]
          .join(" ")
          .toLowerCase()
          .indexOf(keyword) >= 0
      );
    });
  };

  isPairMatchedByKeyword = (pairType, searchKeyword) => {
    const keyword = String(searchKeyword || "")
      .trim()
      .toLowerCase();
    if (!keyword) {
      return true;
    }

    return (
      [pairType.label, pairType.key].join(" ").toLowerCase().indexOf(keyword) >=
      0
    );
  };

  getFilteredPairTypes = () => {
    return this.getFilteredPairTypesFor(
      this.state.activeBase,
      this.state.searchKeyword,
    );
  };

  getTradeSymbol = (pairType) => {
    if (!pairType) {
      return null;
    }

    if (pairType.tradeSymbol) {
      return pairType.tradeSymbol;
    }

    const accountNumber = String(pairType.accountNumber);
    if (TRADE_SYMBOL_OVERRIDES[accountNumber]) {
      return TRADE_SYMBOL_OVERRIDES[accountNumber];
    }

    const base = this.getBaseName(pairType);
    const currency = String(pairType.currency || "").toLowerCase();

    if (base === "BTC") {
      return "BTCUSDc";
    }
    if (base === "XAU") {
      return currency === "standard" ? "XAUUSDm" : "XAUUSDc";
    }
    if (base === "GBPJPY") {
      return "GBPJPY";
    }
    if (base === "EUR") {
      return "EURUSDc";
    }

    return pairType.key || pairType.label || null;
  };

  getTradeData = (accountNumber) => {
    const { messagesTradeDataV2 } = this.context;
    const pairType = this.getPairTypeByAccountNumber(accountNumber);
    const mappedSymbol = this.getTradeSymbol(pairType);
    const key = `${accountNumber}_${mappedSymbol}`;

    if (messagesTradeDataV2 && messagesTradeDataV2[key]) {
      return {
        data: messagesTradeDataV2[key],
        symbol: mappedSymbol,
      };
    }

    const fallbackKey =
      messagesTradeDataV2 &&
      Object.keys(messagesTradeDataV2).find((item) =>
        item.startsWith(`${accountNumber}_`),
      );

    if (fallbackKey) {
      return {
        data: messagesTradeDataV2[fallbackKey],
        symbol: fallbackKey.replace(`${accountNumber}_`, ""),
      };
    }

    return {
      data: null,
      symbol: null,
    };
  };

  handleBaseChange = (event, newValue) => {
    const nextPairTypes = this.getFilteredPairTypesFor(
      newValue,
      this.state.searchKeyword,
    );
    const activeStillVisible = nextPairTypes.some(
      (pairType) => pairType.accountNumber === this.state.activeAccountNumber,
    );

    this.setState({
      activeBase: newValue,
      activeAccountNumber: activeStillVisible
        ? this.state.activeAccountNumber
        : (nextPairTypes[0] && nextPairTypes[0].accountNumber) || "",
    });
  };

  handleTabChange = (event, newValue) => {
    this.setState({ activeAccountNumber: newValue });
  };

  handleSearchChange = (event) => {
    const searchKeyword = event.target.value;
    const keyword = searchKeyword.trim();
    const globalMatches = keyword
      ? this.state.pairTypes.filter((pairType) =>
          this.isPairMatchedByKeyword(pairType, searchKeyword),
        )
      : [];
    const firstGlobalMatch = globalMatches[0];
    const nextActiveBase = firstGlobalMatch
      ? this.getBaseName(firstGlobalMatch)
      : this.state.activeBase;
    const nextPairTypes = this.getFilteredPairTypesFor(
      nextActiveBase,
      searchKeyword,
    );
    const activeStillVisible = keyword
      ? false
      : nextPairTypes.some(
          (pairType) =>
            pairType.accountNumber === this.state.activeAccountNumber,
        );

    this.setState({
      searchKeyword,
      activeBase: nextActiveBase,
      activeAccountNumber: activeStillVisible
        ? this.state.activeAccountNumber
        : (firstGlobalMatch && firstGlobalMatch.accountNumber) ||
          (nextPairTypes[0] && nextPairTypes[0].accountNumber) ||
          this.state.activeAccountNumber,
    });
  };

  formatMoney = (value) => {
    const numeric = Number(value || 0);
    const sign = numeric >= 0 ? "+" : "-";
    return `${sign}$${Math.abs(numeric).toFixed(2)}`;
  };

  formatPlainMoney = (value) => {
    const numeric = Number(value || 0);
    return `$${Math.abs(numeric).toFixed(2)}`;
  };

  formatPercent = (value) => {
    const numeric = Number(value || 0);
    const sign = numeric > 0 ? "+" : "";
    return `${sign}${numeric.toFixed(2)}%`;
  };

  formatCapitalPercent = (value, capital) => {
    const capitalNumber = Number(capital || 0);

    if (!capitalNumber) {
      return null;
    }

    return this.formatPercent((Number(value || 0) / capitalNumber) * 100);
  };

  formatDisplayDate = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(`${value}T00:00:00Z`);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    return `${day} ${month} ${year}`;
  };

  formatDateRange = (startDate, endDate) => {
    const start = this.formatDisplayDate(startDate);
    const end = this.formatDisplayDate(endDate);

    if (start && end) {
      return `${start} - ${end}`;
    }

    return start || end || "";
  };

  formatDisplayDateShort = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(`${value}T00:00:00Z`);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${String(date.getUTCDate()).padStart(2, "0")} ${
      months[date.getUTCMonth()]
    }`;
  };

  formatDateRangeShort = (startDate, endDate) => {
    const start = this.formatDisplayDateShort(startDate);
    const end = this.formatDisplayDateShort(endDate);

    if (start && end) {
      return `${start} - ${end}`;
    }

    return start || end || "";
  };

  getMaxDrawdownValue = (maxFloating) => {
    if (!maxFloating) {
      return 0;
    }

    const preferredKeys = ["LIFE", "12M", "6M", "3M", "1M", "1W", "1D"];
    const matchedKey = preferredKeys.find(
      (key) =>
        maxFloating[key] !== null && typeof maxFloating[key] !== "undefined",
    );

    return matchedKey ? Number(maxFloating[matchedKey] || 0) : 0;
  };

  getMaxDrawdownBadges = (maxFloating, capital) => {
    const badgeItems = [
      { key: "1D", label: "1D" },
      { key: "1W", label: "1W" },
      { key: "1M", label: "1M" },
      { key: "LIFE", label: "Life" },
    ];

    return badgeItems
      .filter(
        ({ key }) =>
          maxFloating &&
          maxFloating[key] !== null &&
          typeof maxFloating[key] !== "undefined",
      )
      .map(({ key, label }) => {
        const value = Number(maxFloating[key] || 0);

        return {
          label,
          value: this.formatMoney(value),
          meta: this.formatCapitalPercent(value, capital),
          color: "#d92d20",
          bg: "#fef3f2",
          border: "#fecdca",
        };
      });
  };

  getValueColor = (
    value,
    positive = "#079455",
    negative = "#d92d20",
    neutral = "#475467",
  ) => {
    const numeric = Number(value);
    if (numeric > 0) return positive;
    if (numeric < 0) return negative;
    return neutral;
  };

  renderMiniTrend = (color, variant = "line") => {
    const bars =
      variant === "bars"
        ? [24, 34, 42, 30, 52, 68, 39, 58, 45, 63, 76, 48]
        : [42, 52, 47, 58, 54, 66, 61, 72, 68, 78];

    return (
      <Box
        sx={{
          width: { xs: 110, md: 130, lg: 150, xl: 170 },
          height: { xs: 44, md: 50, lg: 56, xl: 62 },
          display: { xs: "none", sm: "flex" },
          alignItems: "flex-end",
          justifyContent: "flex-end",
          gap: variant === "bars" ? 0.55 : 0.35,
          opacity: 0.82,
          ml: "auto",
        }}
      >
        {bars.map((height, index) => (
          <Box
            key={`${variant}-${index}`}
            sx={{
              width: variant === "bars" ? 5 : 14,
              height: `${height}%`,
              borderRadius: variant === "bars" ? "6px 6px 0 0" : 2,
              bgcolor: variant === "bars" ? `${color}66` : `${color}24`,
              borderTop: variant === "bars" ? "none" : `3px solid ${color}`,
              transform:
                variant === "bars"
                  ? "none"
                  : `translateY(${index % 2 === 0 ? 5 : 0}px)`,
            }}
          />
        ))}
      </Box>
    );
  };

  renderMetricWave = (color, variant = "line", direction = "up") => {
    const bars =
      variant === "bars"
        ? [20, 28, 38, 50, 68, 46, 60, 76, 90, 64, 82, 56]
        : [88, 78, 82, 62, 44, 52, 28, 16];

    if (variant === "bars") {
      return (
        <Box
          sx={{
            position: "absolute",
            right: { xs: 12, md: 16 },
            bottom: { xs: 50, sm: 68, md: 74, xl: 82 },
            height: { xs: 46, sm: 58, md: 62, xl: 74 },
            width: { xs: 108, sm: 128, md: 138, xl: 164 },
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            gap: 0.65,
            opacity: 0.72,
            pointerEvents: "none",
          }}
        >
          {bars.map((height, index) => (
            <Box
              key={`metric-bars-${index}`}
              sx={{
                width: { xs: 6, sm: 7, xl: 9 },
                height: `${height}%`,
                borderRadius: "8px 8px 0 0",
                bgcolor: `${color}55`,
              }}
            />
          ))}
        </Box>
      );
    }

    if (variant === "sparkline") {
      const path =
        direction === "down"
          ? "M4 10 C28 10 40 20 58 19 C78 18 92 31 124 34"
          : "M4 34 C28 34 40 24 58 25 C78 26 92 13 124 10";

      return (
        <Box
          sx={{
            position: "absolute",
            right: { sm: 16, md: 20, xl: 24 },
            top: { sm: 78, md: 88, xl: 100 },
            width: { sm: 132, md: 152, xl: 176 },
            height: { sm: 46, md: 52, xl: 58 },
            display: { xs: "none", sm: "block" },
            opacity: 0.52,
            pointerEvents: "none",
          }}
        >
          <svg
            viewBox="0 0 132 44"
            width="100%"
            height="100%"
            fill="none"
            preserveAspectRatio="none"
          >
            <path
              d={path}
              stroke={color}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d={`${path} V44 H4 Z`} fill={color} opacity="0.08" />
          </svg>
        </Box>
      );
    }

    const isUp = direction !== "down";

    return (
      <Box
        sx={{
          position: "absolute",
          right: { xs: 12, md: 18 },
          bottom: { xs: 14, md: 16 },
          width: { xs: 130, sm: 150, md: 210, xl: 260 },
          height: { xs: 68, sm: 80, md: 105, xl: 122 },
          display: { xs: "none", sm: "block" },
          opacity: 0.62,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${color}24, ${color}08 72%, transparent)`,
            clipPath: isUp
              ? "polygon(0 100%, 0 86%, 14% 80%, 27% 84%, 40% 58%, 52% 62%, 65% 22%, 79% 58%, 100% 34%, 100% 100%)"
              : "polygon(0 100%, 0 28%, 14% 45%, 27% 38%, 40% 58%, 52% 54%, 65% 72%, 79% 76%, 100% 86%, 100% 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "76%",
            borderBottom: "none",
            background:
              "linear-gradient(90deg, transparent 0 8%, currentColor 8% 12%, transparent 12% 20%, currentColor 20% 24%, transparent 24% 36%, currentColor 36% 40%, transparent 40% 52%, currentColor 52% 56%, transparent 56% 70%, currentColor 70% 74%, transparent 74%)",
            color,
            opacity: 0.2,
            clipPath: isUp
              ? "polygon(0 92%, 16% 78%, 29% 80%, 43% 55%, 55% 59%, 68% 16%, 82% 54%, 100% 28%, 100% 100%, 0 100%)"
              : "polygon(0 24%, 16% 38%, 29% 34%, 43% 54%, 55% 50%, 68% 70%, 82% 76%, 100% 88%, 100% 100%, 0 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "12%",
            height: "70%",
            borderTop: `4px solid ${color}`,
            borderRadius: "52% 48% 0 0",
            transform: isUp ? "skewY(-13deg)" : "skewY(12deg)",
          }}
        />
      </Box>
    );
  };

  renderMetricSkeleton() {
    return (
      <Box
        mt={1.5}
        sx={{
          bgcolor: "#ffffff",
          border: "1px solid #d8e3ef",
          borderRadius: 2,
          p: { xs: 0.8, sm: 1 },
        }}
      >
        <Skeleton height={22} />
        <Grid container spacing={0.75} mt={0.25}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={6} md={3} key={item}>
              <Skeleton height={42} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  renderOrderPanel = (title, order, accentColor, estimatedProfit) => {
    const totalLots = Number(order.totalLots || 0);
    const pointDiff = Number(order.pointDiff || 0);
    const isBuy = title === "Buy";
    const PanelIcon = isBuy ? TrendingUp : TrendingDown;
    const details = [
      {
        label: "TP",
        value: order.takeProfit || "-",
        color: "#1d7edb",
        Icon: TrackChanges,
      },
      {
        label: "Points",
        value: pointDiff,
        color: this.getValueColor(pointDiff, "#f59e0b", "#ef4444"),
        Icon: StackedBarChart,
      },
      {
        label: "Lot",
        value: totalLots.toFixed(2),
        color: "#f59e0b",
        Icon: Layers,
      },
      {
        label: "Est. P/L",
        value: `$${Number(estimatedProfit || 0).toFixed(2)}`,
        color: this.getValueColor(estimatedProfit),
        Icon: Paid,
      },
    ];

    return (
      <Box
        sx={{
          bgcolor: "#ffffff",
          background: `linear-gradient(135deg, #ffffff 0%, ${
            isBuy ? "#f4fff8" : "#fff8f8"
          } 100%)`,
          border: `1px solid ${accentColor}2e`,
          borderRadius: 2,
          p: { xs: 1, sm: 1.1, lg: 1.25, xl: 1.45 },
          height: "100%",
          fontFamily: '"Roboto", "Arial", sans-serif',
          boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 0.75, lg: 1 },
            mb: { xs: 0.85, lg: 1.05 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.75, lg: 0.9 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: 34, sm: 38, lg: 44, xl: 50 },
                height: { xs: 34, sm: 38, lg: 44, xl: 50 },
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: `${accentColor}14`,
                color: accentColor,
                flex: "0 0 auto",
              }}
            >
              <PanelIcon sx={{ fontSize: { xs: 19, lg: 24, xl: 27 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 850,
                  color: accentColor,
                  fontSize: RESPONSIVE_TEXT.detailTitle,
                  lineHeight: 1.05,
                  letterSpacing: 0,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  color: "#667085",
                  fontSize: RESPONSIVE_TEXT.meta,
                  fontWeight: 600,
                  mt: 0.15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {isBuy ? "Open long position" : "Open short position"}
              </Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            label={`${order.totalOrder || 0} orders`}
            sx={{
              height: { xs: 24, lg: 28, xl: 30 },
              bgcolor: `${accentColor}14`,
              color: accentColor,
              border: `1px solid ${accentColor}33`,
              fontWeight: 850,
              fontSize: RESPONSIVE_TEXT.chip,
              "& .MuiChip-label": { px: { xs: 0.75, lg: 1 } },
            }}
          />
        </Box>

        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.86)",
            border: "1px solid #dce8f5",
            borderRadius: 1.5,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            overflow: "hidden",
          }}
        >
          {details.map((item) => (
            <Box
              key={item.label}
              sx={{
                px: { xs: 0.8, lg: 1, xl: 1.15 },
                py: { xs: 0.75, lg: 0.9, xl: 1 },
                borderRight: {
                  xs: "none",
                  sm: "1px solid #dce8f5",
                },
                borderBottom: {
                  xs: "1px solid #dce8f5",
                  sm: "none",
                },
                "&:nth-of-type(2n)": {
                  borderRight: { xs: "none" },
                },
                "&:nth-of-type(n+3)": {
                  borderBottom: { xs: "none" },
                },
                "&:last-child": {
                  borderRight: "none",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.45,
                  minWidth: 0,
                }}
              >
                <item.Icon
                  sx={{
                    color: "#52688c",
                    fontSize: RESPONSIVE_TEXT.icon,
                    flex: "0 0 auto",
                  }}
                />
                <Typography
                  sx={{
                    color: "#667085",
                    fontSize: RESPONSIVE_TEXT.label,
                    fontWeight: 750,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: item.color,
                  fontSize: RESPONSIVE_TEXT.detailValue,
                  fontWeight: 850,
                  mt: 0.45,
                  overflowWrap: "anywhere",
                  lineHeight: 1.1,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  renderMetric(data, profitToday, pairType) {
    if (!data) {
      return this.renderMetricSkeleton();
    }

    const accountNumber = String(
      (pairType && pairType.accountNumber) || data.accountNumber,
    );
    const isCentAccount = pairType && pairType.isCent;
    const converted = {
      ...data,
      balance: Number(data.balance || 0),
      equity: Number(data.equity || 0),
      floating: Number(data.floating || 0),
    };

    if (isCentAccount) {
      converted.balance = converted.balance / 100;
      converted.equity = converted.equity / 100;
      converted.floating = converted.floating / 100;
    }

    const standardCapital = pairType && Number(pairType.capital);
    const floatPercent = standardCapital
      ? ((converted.floating / standardCapital) * 100).toFixed(2)
      : "0.00";
    const groupOrders = (data.order && data.order.tpGroups) || [];
    const groupOrdersBuy = groupOrders.find((o) => o.type === 0) || {};
    const groupOrdersSell = groupOrders.find((o) => o.type === 1) || {};
    const estProfitBuy = isCentAccount
      ? Number(groupOrdersBuy.estimatedProfit || 0) / 100
      : Number(groupOrdersBuy.estimatedProfit || 0);
    const estProfitSell = isCentAccount
      ? Number(groupOrdersSell.estimatedProfit || 0) / 100
      : Number(groupOrdersSell.estimatedProfit || 0);
    const totalLots =
      Number(groupOrdersBuy.totalLots || 0) +
      Number(groupOrdersSell.totalLots || 0);
    const { symbol: tradeSymbol } = this.getTradeData(accountNumber);
    const priceList = data && data.price ? data.price : {};
    let symbolKey = tradeSymbol;

    if (!symbolKey && Object.keys(priceList).length > 0) {
      symbolKey = Object.keys(priceList)[0];
    }

    const priceInfo = priceList[symbolKey];
    const bid = priceInfo && priceInfo.bid != null ? priceInfo.bid : null;
    const ask = priceInfo && priceInfo.ask != null ? priceInfo.ask : null;
    const priceBuy = bid;
    const priceSell = ask;
    let spreadPoint = null;

    if (bid != null && ask != null && priceInfo) {
      const bidNumber = Number(bid);
      const askNumber = Number(ask);
      spreadPoint = Math.round(
        (askNumber - bidNumber) / Number(priceInfo.point || 1),
      );

      if (["EURUSD", "EURUSDc"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100000);
      } else if (["XAUUSD", "XAUUSDc", "XAUUSDm"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100);
      } else if (["BTCUSD", "BTCUSDc", "BTCUSDm"].includes(symbolKey)) {
        spreadPoint = Math.round(askNumber - bidNumber);
      } else if (["GBPJPY", "GBPJPYc", "GBPJPYm"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100);
      } else {
        spreadPoint = Math.round(askNumber - bidNumber);
      }
    }
    const marketUpdatedAt = new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const activeLabel = (pairType && pairType.label) || accountNumber;
    const baseName = this.getBaseName(pairType);
    const pairStyle = this.getPairStyle(pairType);
    const HeaderIcon = this.getPairIcon(pairType);
    const profitOverview =
      this.state.profitOverviewByAccount[accountNumber] || {};
    const maxFloating = this.state.maxFloatingByAccount[accountNumber] || {};
    const maxDrawdown = this.getMaxDrawdownValue(maxFloating);
    const weeklyProfit = profitOverview.weekly || null;
    const monthlyProfit = profitOverview.monthly || null;
    const floatPercentNumber = Number(floatPercent || 0);
    const floatingNumber = Number(converted.floating || 0);
    const floatingIsPositive = floatingNumber > 0;
    const floatingIsNegative = floatingNumber < 0;
    const floatingIsNeutral = !floatingIsPositive && !floatingIsNegative;
    const floatingAccent = floatingIsPositive
      ? "#079455"
      : floatingIsNegative
        ? "#ef4444"
        : "#667085";
    const floatingSoftBg = floatingIsPositive
      ? "#f0fdf4"
      : floatingIsNegative
        ? "#fff7f7"
        : "#f8fafc";
    const floatingIconBg = floatingIsPositive
      ? "#dcfae6"
      : floatingIsNegative
        ? "#ffe4e6"
        : "#eef2f6";
    const floatBadgeIsPositive = floatPercentNumber > 0;
    const floatBadgeIsNegative = floatPercentNumber < 0;
    const floatBadgeColor = this.getValueColor(floatPercentNumber);
    const floatBadgeBg = floatBadgeIsPositive
      ? "#ecfdf3"
      : floatBadgeIsNegative
        ? "#fef3f2"
        : "#f8fafc";
    const floatBadgeBorder = floatBadgeIsPositive
      ? "#abefc6"
      : floatBadgeIsNegative
        ? "#fecdca"
        : "#e4ebf4";
    const profitTodayBadges = [];

    if (weeklyProfit) {
      const weeklyRange = this.formatDateRange(
        weeklyProfit.week_start,
        weeklyProfit.week_end,
      );
      const weeklyRangeShort = this.formatDateRangeShort(
        weeklyProfit.week_start,
        weeklyProfit.week_end,
      );

      profitTodayBadges.push({
        label: weeklyProfit.week_index
          ? `Week ${weeklyProfit.week_index}`
          : "Weekly",
        value: this.formatPlainMoney(weeklyProfit.total_profit),
        meta: this.formatCapitalPercent(
          weeklyProfit.total_profit,
          standardCapital,
        ),
        subMeta: weeklyRange,
        mobileSubMeta: weeklyRangeShort,
        color: this.getValueColor(weeklyProfit.total_profit),
        bg: Number(weeklyProfit.total_profit || 0) >= 0 ? "#ecfdf3" : "#fef3f2",
        border:
          Number(weeklyProfit.total_profit || 0) >= 0 ? "#abefc6" : "#fecdca",
      });
    }

    if (monthlyProfit) {
      const monthlyRangeShort = this.formatDateRangeShort(
        monthlyProfit.period_start,
        monthlyProfit.period_end,
      );

      profitTodayBadges.push({
        label: "Monthly",
        value: this.formatPlainMoney(monthlyProfit.total_profit),
        meta:
          monthlyProfit.percent !== null &&
          typeof monthlyProfit.percent !== "undefined"
            ? this.formatPercent(monthlyProfit.percent)
            : null,
        subMeta:
          monthlyProfit.month_label ||
          this.formatDateRange(
            monthlyProfit.period_start,
            monthlyProfit.period_end,
          ),
        mobileSubMeta: monthlyRangeShort,
        color: this.getValueColor(monthlyProfit.total_profit),
        bg:
          Number(monthlyProfit.total_profit || 0) >= 0 ? "#ecfdf3" : "#fef3f2",
        border:
          Number(monthlyProfit.total_profit || 0) >= 0 ? "#abefc6" : "#fecdca",
      });
    }
    const headerChips = [
      {
        label: baseName,
        color: pairStyle.textColor,
        bg: pairStyle.bgColor,
        border: pairStyle.borderColor,
      },
      {
        label: (pairType && pairType.currency) || "-",
        color: "#92400e",
        bg: "#fff7d6",
        border: "rgba(251,191,36,0.35)",
      },
      {
        label: standardCapital ? `$${standardCapital}` : "-",
        color: "#047857",
        bg: "#e8f7ef",
        border: "rgba(52,211,153,0.35)",
      },
    ];
    const metricItems = [
      {
        label: "Floating P/L",
        mobileLabel: "Floating",
        value: this.formatMoney(converted.floating),
        color: this.getValueColor(converted.floating),
        accent: floatingAccent,
        softBg: floatingSoftBg,
        iconBg: floatingIconBg,
        Icon: floatingIsPositive
          ? TrendingUp
          : floatingIsNegative
            ? TrendingDown
            : ShowChart,
        trend: floatingIsNeutral ? null : "sparkline",
        trendDirection: floatingIsNegative ? "down" : "up",
        badges: [
          {
            label: "Float",
            value: this.formatPercent(floatPercentNumber),
            color: floatBadgeColor,
            bg: floatBadgeBg,
            border: floatBadgeBorder,
          },
        ],
        detailColumns: "minmax(0, 1fr)",
        mobileMinHeight: 116,
        mobileOrder: 1,
        desktopOrder: 1,
      },
      {
        label: "Profit Today",
        mobileLabel: "Profit",
        value:
          profitToday != null && !isNaN(parseFloat(profitToday))
            ? this.formatMoney(profitToday)
            : "-",
        color: this.getValueColor(profitToday || 0),
        accent: "#079455",
        softBg: "#f0fdf4",
        iconBg: "#dcfae6",
        Icon: BarChart,
        valueMeta: this.formatCapitalPercent(profitToday || 0, standardCapital),
        valueMetaIcon: "up",
        badges: profitTodayBadges,
        hideSubMeta: true,
        compactTiles: true,
        detailColumns: "repeat(2, minmax(0, 1fr))",
        mobileDetailColumns: "repeat(2, minmax(0, 1fr))",
        mobileMinHeight: 116,
        mobileOrder: 2,
        desktopOrder: 3,
      },
      {
        label: "Max Drawdown",
        value: this.formatMoney(maxDrawdown),
        valueMeta: this.formatCapitalPercent(maxDrawdown, standardCapital),
        color: "#d92d20",
        accent: "#ef4444",
        softBg: "#fff7f7",
        iconBg: "#ffe4e6",
        Icon: Security,
        valueMetaIcon: "down",
        badges: this.getMaxDrawdownBadges(maxFloating, standardCapital),
        detailColumns: "repeat(2, minmax(0, 1fr))",
        mobileDetailColumns: "repeat(4, minmax(0, 1fr))",
        mobileFull: true,
        mobileMinHeight: 106,
        compactMobile: true,
        compactTiles: true,
        mobileOrder: 4,
        desktopOrder: 4,
      },
      {
        label: "Total Lot",
        value: totalLots.toFixed(2),
        color: "#1769aa",
        accent: "#2e90fa",
        softBg: "#f5fbff",
        iconBg: "#dff1ff",
        Icon: Layers,
        trend: "bars",
        badges: [
          {
            label: "Open",
            value: totalLots.toFixed(2),
            color: "#1769aa",
            bg: "#eff8ff",
            border: "#d1e9ff",
          },
          {
            label: "Buy",
            value: Number(groupOrdersBuy.totalLots || 0).toFixed(2),
            color: "#079455",
            bg: "#ecfdf3",
            border: "#abefc6",
          },
          {
            label: "Sell",
            value: Number(groupOrdersSell.totalLots || 0).toFixed(2),
            color: "#d92d20",
            bg: "#fef3f2",
            border: "#fecdca",
          },
        ],
        detailColumns: "repeat(3, minmax(0, 1fr))",
        mobileDetailColumns: "repeat(3, minmax(0, 1fr))",
        mobileFull: true,
        compactTiles: true,
        mobileMinHeight: 112,
        mobileOrder: 3,
        desktopOrder: 2,
      },
    ];

    return (
      <Box
        mt={0.65}
        sx={{
          bgcolor: "#ffffff",
          borderRadius: 2,
          border: "1px solid #d8e3ef",
          boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
          color: "#1d2939",
          overflow: "hidden",
          fontFamily: '"Roboto", "Arial", sans-serif',
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.2, lg: 1.45, xl: 1.7 },
            py: { xs: 0.85, sm: 1, lg: 1.15, xl: 1.3 },
            background: `linear-gradient(135deg, ${pairStyle.bgColor}, #ffffff 72%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.65,
            borderBottom: "1px solid #dbe7f3",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: 34, md: 40, lg: 46, xl: 52 },
                height: { xs: 34, md: 40, lg: 46, xl: 52 },
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: pairStyle.iconColor,
                color: "#ffffff",
                border: `1px solid ${pairStyle.borderColor}`,
                flex: "0 0 auto",
                boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
              }}
            >
              <HeaderIcon
                sx={{ fontSize: { xs: 18, md: 22, lg: 25, xl: 28 } }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: RESPONSIVE_TEXT.title,
                  fontWeight: 750,
                  lineHeight: 1.1,
                  overflowWrap: "anywhere",
                }}
              >
                {activeLabel}
              </Typography>
              <Typography
                sx={{
                  color: "#667085",
                  fontSize: RESPONSIVE_TEXT.meta,
                  mt: 0.1,
                }}
              >
                {accountNumber}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: { xs: 0.45, lg: 0.7 },
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45 }}>
              {headerChips.map((chip) => (
                <Chip
                  key={chip.label}
                  size="small"
                  label={chip.label}
                  sx={{
                    bgcolor: chip.bg,
                    color: chip.color,
                    border: `1px solid ${chip.border || "rgba(15,23,42,0.08)"}`,
                    fontWeight: 700,
                    height: { xs: 21, md: 23, lg: 25, xl: 27 },
                    fontSize: RESPONSIVE_TEXT.chip,
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 0.85, sm: 1, lg: 1.2, xl: 1.4 },
            py: { xs: 0.8, sm: 0.75, lg: 0.9, xl: 1 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid
            container
            spacing={{ xs: 0.75, sm: 0.75 }}
            sx={{ order: 3, mt: { xs: 0.9, lg: 1 } }}
          >
            {metricItems.map((item) => (
              <Grid
                item
                xs={item.mobileFull ? 12 : 6}
                md={3}
                key={item.label}
                sx={{
                  display: "flex",
                  order: {
                    xs: item.mobileOrder || 0,
                    md: item.desktopOrder || 0,
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: "#ffffff",
                    background: `linear-gradient(135deg, #ffffff 0%, ${
                      item.softBg || "#f8fafc"
                    } 100%)`,
                    border: `1px solid ${item.accent || "#e4ebf4"}22`,
                    borderRadius: 2,
                    px: { xs: 0.75, sm: 1.15, lg: 1.35, xl: 1.55 },
                    py: { xs: 0.75, sm: 1, lg: 1.2, xl: 1.35 },
                    minHeight: {
                      xs: item.mobileMinHeight || (item.mobileFull ? 128 : 112),
                      sm: 150,
                      md: 170,
                      lg: 184,
                      xl: 202,
                    },
                    width: "100%",
                    height: { xs: "auto", sm: "100%" },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: {
                      xs: "flex-start",
                      sm: item.compactMobile ? "flex-start" : "space-between",
                    },
                    boxShadow: "0 12px 26px rgba(15,23,42,0.06)",
                    overflow: "hidden",
                  }}
                >
                  {item.trend
                    ? this.renderMetricWave(
                        item.accent || item.color,
                        item.trend,
                        item.trendDirection,
                      )
                    : null}
                  <Box
                    sx={{
                      minWidth: 0,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: { xs: 0.35, lg: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 0.38, lg: 0.85 },
                          minWidth: 0,
                          flex: "1 1 auto",
                        }}
                      >
                        {item.Icon && (
                          <Box
                            sx={{
                              width: RESPONSIVE_TEXT.metricIcon,
                              height: RESPONSIVE_TEXT.metricIcon,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: item.iconBg || "#edf2f8",
                              color: item.accent || item.color,
                              flex: "0 0 auto",
                              boxShadow: `0 10px 20px ${item.accent || item.color}14`,
                            }}
                          >
                            <item.Icon
                              sx={{
                                fontSize:
                                  "calc(var(--tf-metric-icon-size) * 0.55)",
                              }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.35,
                            minWidth: 0,
                            flex: "1 1 auto",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#1f2a44",
                              fontSize: RESPONSIVE_TEXT.metricTitle,
                              fontWeight: 800,
                              lineHeight: 1.1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.mobileLabel ? (
                              <>
                                <Box
                                  component="span"
                                  sx={{ display: { xs: "inline", sm: "none" } }}
                                >
                                  {item.mobileLabel}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{ display: { xs: "none", sm: "inline" } }}
                                >
                                  {item.label}
                                </Box>
                              </>
                            ) : (
                              item.label
                            )}
                          </Typography>
                          <InfoOutlined
                            sx={{
                              color: "#98a2b3",
                              fontSize: RESPONSIVE_TEXT.metricPill,
                              flex: "0 0 auto",
                              display: { xs: "none", sm: "block" },
                            }}
                          />
                        </Box>
                      </Box>
                      {item.valueMeta && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.35,
                            bgcolor:
                              item.color === "#475467"
                                ? "#f1f5f9"
                                : `${item.color}12`,
                            color: item.color,
                            border: `1px solid ${item.color}18`,
                            borderRadius: 999,
                            px: { xs: 0.38, lg: 0.85 },
                            py: { xs: 0.16, lg: 0.28 },
                            fontSize: RESPONSIVE_TEXT.metricPill,
                            fontWeight: 900,
                            lineHeight: 1.2,
                            flex: "0 0 auto",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.valueMetaIcon === "down" ? (
                            <TrendingDown
                              sx={{
                                fontSize: RESPONSIVE_TEXT.metricPill,
                                display: { xs: "none", sm: "inline-flex" },
                              }}
                            />
                          ) : (
                            <TrendingUp
                              sx={{
                                fontSize: RESPONSIVE_TEXT.metricPill,
                                display: { xs: "none", sm: "inline-flex" },
                              }}
                            />
                          )}
                          {item.valueMeta}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                        gap: 0.65,
                        mt: { xs: 0.45, lg: 1.2 },
                      }}
                    >
                      <Typography
                        sx={{
                          color: item.color,
                          fontSize: {
                            xs: item.mobileFull ? "1.42rem" : "1.18rem",
                            sm: RESPONSIVE_TEXT.metricValue,
                          },
                          fontWeight: 900,
                          lineHeight: 0.98,
                          overflowWrap: "normal",
                          wordBreak: "keep-all",
                          letterSpacing: 0,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      mt: item.compactMobile
                        ? { xs: 0.45, lg: 1.2 }
                        : { xs: 0.45, lg: 1.2 },
                    }}
                  >
                    {item.badges && item.badges.length > 0 && (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs:
                              item.mobileDetailColumns ||
                              item.detailColumns ||
                              "repeat(2, minmax(0, 1fr))",
                            sm:
                              item.detailColumns || "repeat(2, minmax(0, 1fr))",
                          },
                          gap: { xs: 0.34, lg: 0.65 },
                          ...(item.hideDetailsOnMobile
                            ? { display: { xs: "none", sm: "grid" } }
                            : {}),
                        }}
                      >
                        {item.badges.map((badge) => (
                          <Box
                            key={`${item.label}-${badge.label}`}
                            sx={{
                              bgcolor: badge.bg || "#f1f5f9",
                              color: badge.color || "#475467",
                              border: `1px solid ${badge.border || "#e4ebf4"}`,
                              borderRadius: 1.25,
                              px: {
                                xs: item.compactTiles ? 0.42 : 0.48,
                                lg: 0.8,
                                xl: 0.9,
                              },
                              py: {
                                xs: item.compactTiles ? 0.32 : 0.38,
                                lg: 0.65,
                              },
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 0.45,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#344054",
                                  fontSize: RESPONSIVE_TEXT.metricDetailLabel,
                                  fontWeight: 800,
                                  lineHeight: 1.15,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  minWidth: 0,
                                }}
                              >
                                {badge.label}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                flexDirection: "column",
                                gap: 0.08,
                                mt: 0.2,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: badge.color || "#475467",
                                  fontSize: {
                                    xs: item.compactTiles
                                      ? "0.7rem"
                                      : RESPONSIVE_TEXT.metricDetailValue,
                                    sm: RESPONSIVE_TEXT.metricDetailValue,
                                  },
                                  fontWeight: 900,
                                  lineHeight: 1.1,
                                  minWidth: 0,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  overflowWrap: "normal",
                                  wordBreak: "keep-all",
                                }}
                              >
                                {badge.value}
                              </Typography>
                              {badge.meta && (
                                <Typography
                                  sx={{
                                    color: badge.color || "#475467",
                                    fontSize: RESPONSIVE_TEXT.metricPill,
                                    fontWeight: 800,
                                    lineHeight: 1.05,
                                    opacity: 0.82,
                                    whiteSpace: "nowrap",
                                    display: "block",
                                  }}
                                >
                                  {badge.meta}
                                </Typography>
                              )}
                            </Box>
                            {badge.subMeta && !item.hideSubMeta && (
                              <Typography
                                sx={{
                                  color: "#667085",
                                  fontSize: RESPONSIVE_TEXT.metricDate,
                                  fontWeight: 600,
                                  lineHeight: 1.18,
                                  mt: 0.3,
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                }}
                              >
                                {badge.mobileSubMeta ? (
                                  <>
                                    <Box
                                      component="span"
                                      sx={{
                                        display: { xs: "inline", sm: "none" },
                                      }}
                                    >
                                      {badge.mobileSubMeta}
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{
                                        display: { xs: "none", sm: "inline" },
                                      }}
                                    >
                                      {badge.subMeta}
                                    </Box>
                                  </>
                                ) : (
                                  badge.subMeta
                                )}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {priceBuy && priceSell && (
            <Box
              sx={{
                mt: { xs: 0.6, lg: 0.9 },
                bgcolor: "#ffffff",
                background: "linear-gradient(135deg, #ffffff 0%, #f4faff 100%)",
                border: "1px solid #d1e9ff",
                borderRadius: 2,
                px: { xs: 1, sm: 1.15, lg: 1.35, xl: 1.55 },
                py: { xs: 0.85, lg: 1, xl: 1.1 },
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, minmax(0, 1fr))",
                  sm: "1.25fr 0.65fr repeat(3, minmax(0, 1fr))",
                  lg: "1.45fr 0.7fr 1fr 1fr 0.9fr 1.35fr",
                },
                alignItems: "center",
                gap: { xs: 0.7, sm: 1, lg: 1.1 },
                boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
                order: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                  gridColumn: { xs: "1 / span 2", sm: "auto" },
                  order: { xs: 1, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 34, lg: 40, xl: 46 },
                    height: { xs: 34, lg: 40, xl: 46 },
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#e9f5ff",
                    color: "#1769aa",
                    flex: "0 0 auto",
                  }}
                >
                  <ShowChart sx={{ fontSize: { xs: 19, lg: 23, xl: 26 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#1f2a44",
                      fontSize: RESPONSIVE_TEXT.detailTitle,
                      fontWeight: 850,
                      lineHeight: 1.05,
                    }}
                  >
                    Market Snapshot
                  </Typography>
                  <Typography
                    sx={{
                      color: "#667085",
                      fontSize: RESPONSIVE_TEXT.meta,
                      fontWeight: 600,
                      mt: 0.15,
                    }}
                  >
                    Real-time market data
                  </Typography>
                </Box>
              </Box>

              <Chip
                icon={
                  <RadioButtonChecked
                    sx={{ fontSize: `${RESPONSIVE_TEXT.icon} !important` }}
                  />
                }
                label="LIVE"
                sx={{
                  justifySelf: { xs: "start", sm: "center" },
                  alignSelf: "center",
                  order: { xs: 2, sm: 0 },
                  bgcolor: "#e8f7ef",
                  color: "#079455",
                  border: "1px solid #abefc6",
                  fontWeight: 850,
                  height: { xs: 28, lg: 32 },
                  fontSize: RESPONSIVE_TEXT.chip,
                  "& .MuiChip-icon": { color: "#12b76a" },
                  "& .MuiChip-label": { px: 0.85 },
                }}
              />

              <Box
                sx={{
                  borderLeft: { xs: "none", lg: "1px solid #dce8f5" },
                  pl: { xs: 0, lg: 1.1 },
                  order: { xs: 4, sm: 0 },
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                <Typography
                  sx={{
                    color: "#52688c",
                    fontSize: RESPONSIVE_TEXT.label,
                    fontWeight: 800,
                  }}
                >
                  BID
                </Typography>
                <Typography
                  sx={{
                    color: "#1d66d1",
                    fontSize: RESPONSIVE_TEXT.detailTitle,
                    "@media (max-width:599px)": {
                      fontSize: "1rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.05,
                  }}
                >
                  {priceBuy || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  borderLeft: { xs: "none", lg: "1px solid #dce8f5" },
                  pl: { xs: 0, lg: 1.1 },
                  order: { xs: 5, sm: 0 },
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                <Typography
                  sx={{
                    color: "#52688c",
                    fontSize: RESPONSIVE_TEXT.label,
                    fontWeight: 800,
                  }}
                >
                  ASK
                </Typography>
                <Typography
                  sx={{
                    color: "#1d66d1",
                    fontSize: RESPONSIVE_TEXT.detailTitle,
                    "@media (max-width:599px)": {
                      fontSize: "1rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.05,
                  }}
                >
                  {priceSell || "-"}
                </Typography>
              </Box>

              <Chip
                label={`Spread ${spreadPoint != null ? spreadPoint : "-"} pts`}
                sx={{
                  justifySelf: { xs: "start", lg: "center" },
                  width: { xs: "100%", sm: "auto" },
                  order: { xs: 6, sm: 0 },
                  bgcolor: "#e9f5ff",
                  color: "#175cd3",
                  border: "1px solid #d1e9ff",
                  fontWeight: 850,
                  height: { xs: 28, lg: 32 },
                  fontSize: RESPONSIVE_TEXT.chip,
                  "& .MuiChip-label": { px: 1 },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.45,
                  gridColumn: { xs: "1 / -1", lg: "auto" },
                  order: { xs: 3, lg: 0 },
                  justifySelf: { xs: "start", lg: "end" },
                  color: "#667085",
                  minWidth: 0,
                }}
              >
                <AccessTime
                  sx={{ fontSize: RESPONSIVE_TEXT.icon, flex: "0 0 auto" }}
                />
                <Typography
                  sx={{
                    fontSize: RESPONSIVE_TEXT.meta,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Updated: {marketUpdatedAt}
                </Typography>
              </Box>
            </Box>
          )}

          <Grid
            container
            spacing={0.9}
            sx={{ mt: { xs: 0.75, lg: 0.9 }, order: 2 }}
          >
            <Grid item xs={6}>
              {this.renderOrderPanel(
                "Buy",
                groupOrdersBuy,
                "#22c55e",
                estProfitBuy,
              )}
            </Grid>
            <Grid item xs={6}>
              {this.renderOrderPanel(
                "Sell",
                groupOrdersSell,
                "#ef4444",
                estProfitSell,
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  render() {
    const {
      activeAccountNumber,
      pairTypes,
      isLoadingPairTypes,
      pairTypesError,
      activeBase,
      searchKeyword,
    } = this.state;
    const activePairType = this.getPairTypeByAccountNumber(activeAccountNumber);
    const profitToday = this.getProfitToday(activeAccountNumber);
    const metricData = this.getMetricDataForAccount(activeAccountNumber);
    const baseOptions = this.getBaseOptions();
    const filteredPairTypes = this.getFilteredPairTypes();
    const activePairVisible = filteredPairTypes.some(
      (pairType) => pairType.accountNumber === activeAccountNumber,
    );

    if (isLoadingPairTypes) {
      return (
        <Box
          sx={{
            ...TAB_FLOATING_FONT_VARS,
            bgcolor: "#f7f9fc",
            border: "1px solid #dce3ee",
            borderRadius: 2,
            p: { xs: 0.75, sm: 0.9, lg: 1.05, xl: 1.2 },
          }}
        >
          <Skeleton height={36} />
          <Skeleton height={32} sx={{ mt: 0.6 }} />
          {this.renderMetricSkeleton()}
        </Box>
      );
    }

    if (pairTypesError || pairTypes.length === 0) {
      return (
        <Box
          mt={2}
          sx={{
            ...TAB_FLOATING_FONT_VARS,
            bgcolor: "#1e2f4d",
            borderRadius: 2,
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2 },
            color: "#fff",
          }}
        >
          <Typography sx={{ fontSize: RESPONSIVE_TEXT.title }}>
            {pairTypesError || "Pair type data belum tersedia."}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          ...TAB_FLOATING_FONT_VARS,
          bgcolor: "#f7f9fc",
          border: "1px solid #dce3ee",
          borderRadius: 2,
          p: { xs: 0.75, sm: 0.9, lg: 1.05, xl: 1.2 },
          fontFamily: '"Roboto", "Arial", sans-serif',
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e5eaf2",
            borderRadius: 2,
            p: { xs: 0.45, md: 0.55, lg: 0.65, xl: 0.75 },
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.6, md: 0.8, lg: 1 },
            flexWrap: { xs: "wrap", lg: "nowrap" },
            boxShadow: "0 8px 18px rgba(15,23,42,0.05)",
          }}
        >
          <Tabs
            value={activeBase}
            onChange={this.handleBaseChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: { xs: 34, md: 40, lg: 46, xl: 52 },
              flex: "1 1 auto",
              minWidth: 0,
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: { xs: 30, md: 36, lg: 42, xl: 48 },
                borderRadius: 1.5,
                color: "#42526e",
                fontFamily: '"Roboto", "Arial", sans-serif',
                fontWeight: 800,
                px: { xs: 0.85, sm: 1.1, md: 1.35, lg: 1.65, xl: 1.9 },
                border: "1px solid #e8edf5",
                boxShadow: "0 6px 14px rgba(15,23,42,0.04)",
                fontSize: RESPONSIVE_TEXT.baseTab,
              },
              "& .Mui-selected": {
                color: "inherit",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {baseOptions.map((base) => {
              const config = this.getBaseConfig(base.value);
              const Icon = config.Icon;
              const selected = activeBase === base.value;

              return (
                <Tab
                  key={base.value}
                  value={base.value}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.55 }}
                    >
                      <Icon
                        sx={{
                          color: selected ? config.color : "#667085",
                          fontSize: RESPONSIVE_TEXT.icon,
                        }}
                      />
                      <span>{base.label}</span>
                      <Chip
                        size="small"
                        label={base.count}
                        sx={{
                          height: { xs: 19, md: 22, lg: 24, xl: 26 },
                          minWidth: { xs: 24, md: 27, lg: 30, xl: 32 },
                          bgcolor: selected ? config.color : config.bg,
                          color: selected ? "#fff" : config.color,
                          border: `1px solid ${
                            selected ? "rgba(255,255,255,0.55)" : config.border
                          }`,
                          boxShadow: selected
                            ? "0 3px 8px rgba(15,23,42,0.18)"
                            : "none",
                          fontWeight: 800,
                          fontSize: RESPONSIVE_TEXT.chip,
                          lineHeight: 1,
                          "& .MuiChip-label": {
                            px: 0.65,
                            minWidth: 12,
                            textAlign: "center",
                          },
                        }}
                      />
                    </Box>
                  }
                  sx={{
                    bgcolor: selected ? config.bg : "#ffffff",
                    color: selected ? `${config.color} !important` : "#42526e",
                    border: selected
                      ? `1px solid ${config.border}`
                      : "1px solid #e8edf5",
                    boxShadow: selected
                      ? "0 10px 22px rgba(15,23,42,0.1)"
                      : "0 6px 14px rgba(15,23,42,0.04)",
                    "&.Mui-selected": {
                      bgcolor: config.bg,
                      color: `${config.color} !important`,
                    },
                  }}
                />
              );
            })}
          </Tabs>

          <TextField
            value={searchKeyword}
            onChange={this.handleSearchChange}
            placeholder="Search master..."
            size="small"
            sx={{
              width: { xs: "100%", md: 340, lg: 460, xl: 560 },
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f8fafc",
                borderRadius: 1.5,
                fontSize: RESPONSIVE_TEXT.pairTab,
                fontWeight: 600,
                height: { xs: 36, md: 40, lg: 44, xl: 48 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{ color: "#98a2b3", fontSize: RESPONSIVE_TEXT.icon }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredPairTypes.length === 0 ? (
          <Box
            sx={{
              mb: 0.4,
              borderRadius: 2,
              bgcolor: "#edf3fa",
              border: "1px solid #e1e9f3",
              color: "#667085",
              fontSize: RESPONSIVE_TEXT.pairTab,
              fontWeight: 700,
              px: 1.25,
              py: 1,
            }}
          >
            No pair types found for the selected base and search keyword.
          </Box>
        ) : (
          <Tabs
            value={activePairVisible ? activeAccountNumber : false}
            onChange={this.handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 0.4,
              borderRadius: 2,
              bgcolor: "#edf3fa",
              border: "1px solid #e1e9f3",
              px: 0.55,
              minHeight: { xs: 34, md: 40, lg: 46, xl: 52 },
              "& .MuiTabs-flexContainer": {
                gap: 0.55,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: '"Roboto", "Arial", sans-serif',
                fontWeight: 700,
                borderRadius: 1.5,
                minHeight: { xs: 28, md: 34, lg: 40, xl: 46 },
                maxWidth: { xs: 140, sm: 190, lg: 220, xl: 260 },
                color: "#334155",
                fontSize: RESPONSIVE_TEXT.pairTab,
                px: { xs: 0.9, sm: 1.15, md: 1.35, lg: 1.55, xl: 1.75 },
                my: 0.35,
                overflow: "hidden",
              },
              "& .Mui-selected": {
                color: "inherit",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {filteredPairTypes.map((pairType) => {
              const pairStyle = this.getPairStyle(pairType);
              const PairIcon = this.getPairIcon(pairType);
              const selected = pairType.accountNumber === activeAccountNumber;

              return (
                <Tab
                  key={pairType.accountNumber}
                  value={pairType.accountNumber}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.45,
                        minWidth: 0,
                      }}
                    >
                      <PairIcon
                        sx={{
                          color: selected ? pairStyle.iconColor : "#667085",
                          fontSize: RESPONSIVE_TEXT.icon,
                        }}
                      />
                      <Box sx={{ minWidth: 0, textAlign: "left" }}>
                        <span>{pairType.label}</span>
                      </Box>
                    </Box>
                  }
                  sx={{
                    bgcolor: selected ? pairStyle.bgColor : "transparent",
                    color: selected
                      ? `${pairStyle.textColor} !important`
                      : "#334155",
                    border: selected
                      ? `1px solid ${pairStyle.borderColor}`
                      : "1px solid transparent",
                    boxShadow: selected
                      ? "0 4px 10px rgba(15,23,42,0.08)"
                      : "none",
                    "&.Mui-selected": {
                      bgcolor: pairStyle.bgColor,
                      color: `${pairStyle.textColor} !important`,
                    },
                  }}
                />
              );
            })}
          </Tabs>
        )}

        <Box p={0} mb={0.25}>
          {this.renderMetric(metricData, profitToday, activePairType)}
        </Box>
      </Box>
    );
  }
}

export default AccountMetricTabs;
